import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  message,
} from "antd";

import {
  FileProtectOutlined,
  SafetyOutlined,
  DollarOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import {
  HEADERS_EXPORT_DETAIL,
  POST_MAIN_SALE_IMPOUND,
  POST_MAIN_SELLING_CAR_IMPOUND,
} from "../../API/apiUrls";
import MotionHoc from "../../../utils/MotionHoc";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import DateCustom from "../../../hook/DateCustom";
import ApprovedInvoiceAndReceiptCarImpound from "./modal/ApprovedInvoiceAndReceiptCarImpound";
import CheckInvoiceAndReceiptCarImpound from "./modal/CheckInvoiceAndReceiptCarImpound";
import ExportDocModal from "./modal/ExportDocModal";
import dayjs from "dayjs";

// ─── Constants ────────────────────────────────────────────────────
const BRAND = {
  primary: "#efa420",
  primaryLight: "#ffcb05",
  panel: "#fffdf0",
  panelLight: "#fff3cc",
  panelBorder: "#ffcb05",
};

// ─── Helpers ──────────────────────────────────────────────────────
const normalize = (v) =>
  String(v ?? "")
    .trim()
    .toLowerCase();

const getFullName = (r) =>
  `${r.SNAM ?? ""}${r.NAME1 ?? ""} ${r.NAME2 ?? ""}`.trim();

const matchesSearch = (item, query) => {
  if (!query) return true;
  const fields = [
    item.CONTNO,
    item.newContno,
    item.CUSCOD,
    item.NAME1,
    item.NAME2,
    getFullName(item),
  ];
  return fields.some((v) => normalize(v).includes(query));
};

// ─── StatusChip ───────────────────────────────────────────────────
const StatusChip = ({ icon, label, count, isActive, disabled, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "6px 14px",
      borderRadius: 20,
      border: `1.5px solid ${isActive ? BRAND.primary : "#d9d9d9"}`,
      background: isActive
        ? `linear-gradient(135deg, ${BRAND.primaryLight}, ${BRAND.primary})`
        : "#fff",
      color: isActive ? "#fff" : "#555",
      fontWeight: isActive ? 600 : 400,
      fontSize: 13,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      boxShadow: isActive ? "0 2px 8px rgba(239,164,32,0.45)" : "none",
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    }}
  >
    <span>{icon}</span>
    <span>{label}</span>
    {count !== null && (
      <span
        style={{
          marginLeft: 4,
          background: isActive ? "rgba(255,255,255,0.35)" : BRAND.panelLight,
          borderRadius: 10,
          padding: "0 6px",
          fontSize: 11,
          fontWeight: 700,
          color: isActive ? "#fff" : BRAND.primary,
        }}
      >
        {count}
      </span>
    )}
  </button>
);

// ─── Main ─────────────────────────────────────────────────────────
const Main = () => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const [convertDateThai] = DateCustom();
  const roleId = localStorage.getItem("ROLE_ID");

  const [isModalSellingCar, setIsModalSellingCar] = useState(false);
  const [isModalPrint, setIsModalPrint] = useState(false);
  const [dataRecord, setDataRecord] = useState(null);
  const [isModalDocPrint, setIsModalDocPrint] = useState(false);

  // ✅ เลือก API ตาม roleId ครั้งเดียว ใช้ร่วมกันทุกจุดที่เรียก fetchByStatus
  const useApi = useMemo(
    () =>
      roleId === "6" ? POST_MAIN_SALE_IMPOUND : POST_MAIN_SELLING_CAR_IMPOUND,
    [roleId],
  );

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 22, icon: "📤", label: "ยึดรอไถ่ถอน" },
      { value: 23, icon: "🕒", label: "ยึดขาด" },
      { value: 24, icon: "✅", label: "รอขายทอดตลาด" },
      { value: 25, icon: "🔄", label: "ขายทอดตลาดแล้ว" },
    ],
    [],
  );

  const visibleStatuses = useMemo(() => {
    return STATUS_OPTIONS.filter((opt) => {
      if (roleId === "6") {
        return opt.value === 24 || opt.value === 25; // roleId "6" เห็นแค่ 24, 25
      }
      return true; // roleId "11" หรืออื่นๆ เห็นทั้งหมด
    });
  }, [STATUS_OPTIONS, roleId]);

  // State
  const [statusCache, setStatusCache] = useState({}); // { [status]: data[] }
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const [processStatus, setProcessStatus] = useState(() => {
    return roleId === "6" ? 24 : 24;
  });

  const fetchByStatus = useCallback(
    async (status, forceRefresh = false, uselApi) => {
      setLoading(true);
      try {
        const res = await axios.post(
          uselApi,
          { userId: 1, currentStatus: status },
          { headers: HEADERS_EXPORT_DETAIL },
        );
        const result = res.data?.data?.length > 0 ? res.data.data : [];

        setStatusCache((prev) => ({ ...prev, [status]: result }));
        setDataArr(result);
        if (!result.length) message.info("ไม่มีข้อมูล");
      } catch (error) {
        if (error.name === "CanceledError") return;
        message.error(
          error.response?.data?.message ||
            error.message ||
            "ไม่สามารถโหลดข้อมูลได้",
        );
        setDataArr([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    setProcessStatus(24);
    fetchByStatus(24, false, useApi); // ✅ ส่ง useApi เข้า argument ตัวที่ 3
  }, [fetchByStatus, useApi]);

  const handleChangeStatus = useCallback(
    (value) => {
      if (value === processStatus || loading) return;
      setSearchInput("");
      setProcessStatus(value);
      fetchByStatus(value, false, useApi); // ✅ ส่ง useApi
    },
    [processStatus, loading, fetchByStatus, useApi],
  );

  const handleReload = useCallback(
    () => fetchByStatus(processStatus, true, useApi), // ✅ ส่ง useApi
    [processStatus, fetchByStatus, useApi],
  );

  const filteredData = useMemo(() => {
    if (!dataArr.length) return [];
    const query = normalize(searchInput);
    return dataArr.filter((item) => matchesSearch(item, query));
  }, [dataArr, searchInput]);

  const columns = useMemo(
    () =>
      [
        {
          title: "ลำดับ",
          key: "index",
          align: "center",
          width: 80,
          render: (_, __, i) => i + 1,
        },
        {
          title: "เลขสัญญา",
          dataIndex: "CONTNO",
          key: "CONTNO",
          align: "center",
          width: 150,
        },
        {
          title: "เลขสัญญาใหม่",
          dataIndex: "newContno",
          key: "newContno",
          align: "center",
          width: 150,
        },
        {
          title: "ชื่อ-นามสกุล",
          key: "fullName",
          align: "center",
          render: (_, r) => getFullName(r),
        },
        {
          title: "วันที่ยึด",
          dataIndex: "impoundDate",
          key: "impoundDate",
          align: "center",
          render: (_, r) =>
            r.impoundDate ? dayjs(r.impoundDate).format("DD MMM YY") : null,
        },
        {
          title: "วันที่ขาย",
          dataIndex: "sold_out_date",
          key: "sold_out_date",
          align: "center",
          render: (_, r) =>
            r.sold_out_date ? dayjs(r.sold_out_date).format("DD MMM YY") : null,
        },
        {
          title: "ราคาขาย",
          key: "sold_out_price",
          align: "center",
          render: (_, r) => {
            return (
              <Tag color={r.sold_out_price ? "blue" : "white"}>
                {currencyFormatPoint(r.sold_out_price || 0)}
              </Tag>
            );
          },
        },
        // processStatus === 22 && {
        //   title: "การจัดการ",
        //   key: "processStatus",
        //   align: "center",
        //   width: 180,
        //   render: (_, record) => (
        //     <Space size="middle">
        //       <>
        //         <Button
        //           variant="outlined"
        //           color="default"
        //           icon={<SafetyOutlined />}
        //           onClick={() => {
        //             console.log(record);
        //             setDataRecord(record);
        //             setIsModalDocPrint(true);
        //           }}
        //         >
        //           ดูรายละเอียด
        //         </Button>
        //       </>
        //     </Space>
        //   ),
        // },

        (processStatus === 24 || processStatus === 25) && {
          title: "การจัดการ",
          key: "processStatus",
          align: "center",
          width: 180,
          render: (_, record) => (
            <Space size="middle">
              {processStatus === 24 && (roleId === "1" || roleId === "11") && (
                <>
                  <Button
                    variant="outlined"
                    color={!record.check_doc_ad ? "blue" : "orange"}
                    icon={<SafetyOutlined />}
                    onClick={() => {
                      setDataRecord(record);
                      setIsModalPrint(true);
                    }}
                    disabled={record.flag_fn}
                  >
                    {!record.check_doc_ad ? "ตรวจสอบ" : "แก้ไข"}
                  </Button>
                </>
              )}
              {processStatus === 24 &&
                (roleId === "1" || roleId === "6") &&
                !record.TMBILL_DATE && (
                  <Button
                    variant="outlined"
                    color="blue"
                    icon={<DollarOutlined />}
                    onClick={() => {
                      setDataRecord(record);
                      setIsModalSellingCar(true);
                    }}
                  >
                    รับเงิน
                  </Button>
                )}
              {/* ((record.check_doc_ad && (roleId === "1" || roleId === "11")) ||
                  (record.TMBILL_DATE &&
                    (roleId === "1" || roleId === "6"))) && ( */}
              {/* {(processStatus === 24 || processStatus === 25) &&
                ((record.check_doc_ad && (roleId === "1" || roleId === "11")) ||
                  (record.TMBILL_DATE &&
                    (roleId === "1" || roleId === "6"))) && ( */}
              <Button
                variant="outlined"
                icon={<FileProtectOutlined />}
                onClick={() => {
                  setDataRecord(record);
                  setIsModalSellingCar(true);
                }}
              >
                ใบเสร็จ
              </Button>
              {/* )} */}
            </Space>
          ),
        },
      ].filter(Boolean),
    [processStatus, roleId, currencyFormatPoint],
  );

  return (
    <>
      <Card
        style={{
          marginBottom: 8,
          borderRadius: 12,
          border: `1px solid ${BRAND.panelBorder}`,
          background: BRAND.panel,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              background: BRAND.panelLight,
              border: `1px solid ${BRAND.panelBorder}`,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 14, color: BRAND.primary }}>🔍</span>
            <span
              style={{ fontSize: 13, fontWeight: 500, color: BRAND.primary }}
            >
              เลือกสถานะ
            </span>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {/* 🛠️ เปลี่ยนการ Map จาก STATUS_OPTIONS เดิมมาใช้ visibleStatuses ที่กรองสิทธิ์แล้ว */}
            {visibleStatuses.map((opt) => {
              const isActive = processStatus === opt.value;
              const cached = statusCache[opt.value];
              const count = isActive
                ? filteredData.length
                : (cached?.length ?? null);
              return (
                <StatusChip
                  key={opt.value}
                  {...opt}
                  count={count}
                  isActive={isActive}
                  disabled={loading}
                  onClick={() => handleChangeStatus(opt.value)}
                />
              );
            })}
          </div>

          {loading && (
            <span
              style={{
                marginLeft: "auto",
                color: BRAND.primary,
                fontSize: 12,
                whiteSpace: "nowrap",
              }}
            >
              ⏳ กำลังโหลด...
            </span>
          )}
        </div>
      </Card>

      {/* ── Table Card ── */}
      <Card>
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Row gutter={[0, 16]}>
            <Col span={24} style={{ textAlign: "end" }}>
              <Space size={10} wrap>
                <Input
                  placeholder="ค้นหาเลขสัญญา, ชื่อ"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ width: 250, borderRadius: 8 }}
                  allowClear
                  suffix={<span style={{ marginRight: 5 }}>🔍</span>}
                  size="small"
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setSearchInput("")}
                  disabled={!searchInput}
                  style={{
                    borderRadius: 8,
                    backgroundColor: "#fff1f0",
                    borderColor: "#ffa39e",
                    color: "#cf1322",
                  }}
                >
                  ล้างตัวกรอง
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReload}
                  disabled={loading}
                  style={{
                    borderRadius: 8,
                    borderColor: BRAND.primary,
                    color: BRAND.primary,
                  }}
                >
                  รีเฟรช
                </Button>
              </Space>
            </Col>

            <Col span={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={filteredData}
                rowKey={(r) => r.CONTNO || r.id}
                scroll={{ x: 1000 }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                footer={() => (
                  <p style={{ margin: 0 }}>
                    จำนวนสัญญาทั้งหมด {filteredData.length}
                  </p>
                )}
              />
            </Col>
          </Row>
        </Spin>
      </Card>
      {isModalSellingCar && (
        <ApprovedInvoiceAndReceiptCarImpound
          open={isModalSellingCar}
          close={setIsModalSellingCar}
          record={dataRecord}
          handleLoad={handleReload}
        />
      )}
      {isModalPrint && (
        <CheckInvoiceAndReceiptCarImpound
          open={isModalPrint}
          close={setIsModalPrint}
          record={dataRecord}
          handleLoad={handleReload}
        />
      )}
      {isModalDocPrint && (
        <ExportDocModal
          open={isModalDocPrint}
          close={setIsModalDocPrint}
          record={dataRecord}
          handleLoad={handleReload}
        />
      )}
    </>
  );
};

const MainApprovedInvoiceAndReceiptCarImpound = MotionHoc(Main);
export default MainApprovedInvoiceAndReceiptCarImpound;
